import React, { useRef, useState } from 'react';
import { StyleSheet } from 'react-native';
import { ThemeContext } from '../../../module/ThemeManager';
import { Box, Container, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import backFilled from '../../../asset/images/icons/es_arrow_back.svg';
import talentsOutline from '../../../asset/images/icons/talents_big.svg';
import { ColorScheme, theme } from '../../../constants/theme';
import { TalentsCard } from '../../EsCards/TalentsCard';
import { ITalentData } from '../../../redux/talent/types';
import { isEmpty } from 'lodash';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { SearchIcon } from '@chakra-ui/icons';
import { makeStyles } from '@mui/styles';
import close from '../../../asset/images/icons/es_x_close.svg';
import { ITalentDetails } from '../../TalentEditor/TalentEditor';
import { Spinner } from '@chakra-ui/react';
import { ViewToggler } from '../../ViewToggler/ViewToggler';
import { ColumnsData, Table } from '../../Tables/Table';
import { useThemeContext } from '../../../hook/useThemeManager';
import UserIcon from '../../CustomSVGIcons/UserIcon';
import { LocationMarker } from '../../CustomSVGIcons/LocationMarker';
import CalendarIcon from '../../CustomSVGIcons/CalendarIcon';
import { BookAndGlassesIcon } from '../../CustomSVGIcons/BookAndGlassesIcon';
import { CustomChipGroup } from '../../Chip/CustomChipGroup';
import { EsEmptyContentCard } from '../../EsCards/EsEmptyContentCard';
import { useDispatch } from 'react-redux';
import { navigate } from '../../../redux/navigator/action';
import CustomLazyLoadImage from '../../Image/CustomLazyLoadImage';
import { avatar_female, avatar_male } from '../../../asset/images';

export interface IEditTalentPayload extends ITalentDetails {
    id: string;
}
interface ITalentsContainerInterface {
    talentData?: ITalentData[];
    handleUserSuggestionClick?: (talent: ITalentData) => void;
    onSaveButtonClick?: (talentData: ITalentData) => void;
    onDeleteTalent?: (talentData: ITalentData) => void;
    loading: boolean;
    searchTerm: string;
    onTalentSearchClear: () => void;
    onChange: (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    onEditTalent: (details: IEditTalentPayload) => void;
}

const useStyles = makeStyles({
    container: {
        alignItems: 'center',
        padding: 10,
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            borderRadius: '50px', // Adjust the value to control the roundness of the border
            '& fieldset': {
                borderColor: 'grey',
            },
            '&:hover fieldset': {
                borderColor: 'grey',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'grey',
            },
            '& .MuiInputBase-input': {
                maxHeight: '30px',
                overflow: 'hidden',
                color: 'white',
                fontSize: '12px',
            },
        },
    },
    iconBadge: {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        borderRadius: '50%', // Make the badge have a round border radius
        padding: '5px', // Add padding to adjust the size of the badge
        marginLeft: '10px',
    },
    icon: {
        color: 'white',
    },
});

const TalentsContainer: React.FC<ITalentsContainerInterface> = props => {
    const {
        talentData,
        handleUserSuggestionClick,
        onDeleteTalent,
        onTalentSearchClear,
        onChange,
        searchTerm,
        onEditTalent,
        loading: loadingSearch,
    } = props;
    const { themeStyle, view } = useThemeContext();
    const [loadingData, setLoadingData] = useState(false);
    const classes = useStyles();
    const inputRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    const navigateBack = () => {
        dispatch(navigate({ routes: -1 }));
    };

    const handleLoadingData = (loading: boolean) => {
        setLoadingData(loading);
    };

    const NoTalentsData = () => {
        return (
            <Box
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    minHeight: view === 'grid' ? '70vh' : '0%',
                }}>
                <EsEmptyContentCard
                    title="No Results Found"
                    description="We could not find any results for your query. Please try again."
                    buttonText="Go Back"
                    filledIcon={backFilled}
                    filledIconAlt="Back icon filled"
                    outlineIcon={talentsOutline}
                    outlineIconAlt="Back icon outline"
                    onClickHandle={navigateBack}
                />
            </Box>
        );
    };

    const renderCustomImage = (params: any) => {
        return (
            <CustomLazyLoadImage
                key={params._id}
                src={
                    params.talent.profile_picture
                        ? params.talent.profile_picture
                        : params.talent.gender === 'female'
                        ? avatar_female
                        : avatar_male
                }
                gradientStyle={{
                    borderRadius: 3,
                }}
                style={{
                    height: '65px',
                    minWidth: '60px',
                    width: '80px',
                    maxWidth: '100px',
                    border: `0.1px solid white`,
                    borderRadius: '10px',
                    objectFit: 'cover',
                    opacity: 1,
                    cursor: 'pointer',
                }}
                alt={params.title}
                onClick={() => handleUserSuggestionClick && handleUserSuggestionClick(params)}
            />
        );
    };

    const renderCustomFullName = (params: any) => {
        return (
            <Typography
                style={{
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: 13.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: theme[themeStyle].tag_white,
                }}>
                {params.talent.firstName} {params.talent.lastName}
            </Typography>
        );
    };

    const renderCustomUserName = (params: any) => {
        return (
            <Typography
                style={{
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: 13.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: theme[themeStyle].tag_white,
                }}>
                {params.talent.userName}
            </Typography>
        );
    };

    const renderCustomLocation = (params: any) => {
        return (
            <Typography
                style={{
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: 13.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: theme[themeStyle].tag_white,
                }}>
                {params.talent.location.description}
            </Typography>
        );
    };

    const renderCustomTags = (params: any) => {
        return (
            <CustomChipGroup
                tagSx={{
                    px: '7px',
                    borderRadius: 13,
                    fontSize: 14,
                }}
                maxVisible={3}
                withNiceColor
                tags={params.tags}
            />
        );
    };

    const renderCustomActions = (params: any) => {
        return (
            <TalentsCard
                talent={params}
                showIconOnly
                iconSx={{
                    transform: 'rotate(90deg)',
                }}
                onClickUserCard={() => handleUserSuggestionClick && handleUserSuggestionClick(params)}
                onDeleteTalent={onDeleteTalent}
                onEditTalent={({ note, tags }) => onEditTalent({ note, tags, id: params._id })}
            />
        );
    };

    const columnsData: ColumnsData[] = [
        {
            field: 'image',
            label: '',
            type: 'custom',
            customCell: renderCustomImage,
            sortable: false,
            width: 100,
        },
        {
            field: 'fullName',
            label: 'Full Name',
            type: 'custom',
            customCell: renderCustomFullName,
            sortable: true,
            width: 350,
        },
        {
            field: 'userName',
            label: 'User Name',
            type: 'custom',
            customCell: renderCustomUserName,
            sortable: true,
            width: 300,
            headerIcon: <UserIcon stroke={theme[themeStyle].tableIcon} style={{ width: '2em', height: '2em' }} />,
        },
        {
            field: 'location',
            label: 'Location',
            type: 'custom',
            customCell: renderCustomLocation,
            sortable: true,
            width: 250,
            headerIcon: <LocationMarker style={{ width: '2em', height: '2em', color: theme[themeStyle].tableIcon }} />,
        },
        {
            field: 'createdAt',
            label: 'User Since',
            type: 'text',
            textType: 'date',
            sortable: true,
            width: 250,
            headerIcon: <CalendarIcon style={{ width: '2em', height: '2em' }} fill={theme[themeStyle].tableIcon} />,
        },
        {
            field: 'tags',
            label: 'Tags',
            type: 'custom',
            customCell: renderCustomTags,
            sortable: false,
            width: 300,
            headerIcon: (
                <BookAndGlassesIcon style={{ width: '2em', height: '2em', color: theme[themeStyle].tableIcon }} />
            ),
        },
        {
            field: 'actions',
            label: 'Actions',
            type: 'custom',
            customCell: renderCustomActions,
            sortable: false,
            width: 80,
            alignColumn: 'right',
        },
    ];

    return (
        <ThemeContext.Consumer>
            {({ themeStyle, view }) => {
                const styles = dynamicStyles(themeStyle);

                return (
                    <Container
                        disableGutters
                        maxWidth={false}
                        style={styles.container}
                        sx={{
                            zIndex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            gap: '50px',
                            width: '100%',
                            overflowX: 'hidden',
                            marginTop: { xs: '0px', sm: '40px' },
                            marginLeft: { xs: '0px', sm: '65px' },
                            paddingLeft: { xs: '16px', sm: '40px' },
                            paddingRight: { xs: '16px', sm: '40px' },
                            paddingTop: { xs: '40px', sm: '0px' },
                        }}>
                        <Box
                            sx={{
                                width: '100%',
                                height: 'auto',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                gap: { xs: '26px', lg: '36px' },
                            }}>
                            <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center">
                                <Typography style={styles.text}>{'Talents'}</Typography>
                                <Box>
                                    <TextField
                                        autoFocus
                                        variant="outlined"
                                        placeholder={'Search talent...'}
                                        color="secondary"
                                        ref={inputRef}
                                        value={searchTerm}
                                        InputProps={{
                                            placeholder: `Search talent...`,
                                            sx: {
                                                color: theme[themeStyle].lightWhite,
                                                fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                fontSize: 14,
                                                fontWeight: '300',
                                                borderRadius: '50px',
                                                paddingLeft: '8px',
                                                backgroundColor: theme[themeStyle].gray_text,
                                                width: { xs: 200, sm: 300 },
                                                height: 42,
                                                cursor: 'pointer',
                                            },
                                            startAdornment: (
                                                <InputAdornment
                                                    sx={{ paddingLeft: 1 }}
                                                    position="start"
                                                    className={classes.icon}>
                                                    <SearchIcon sx={{ color: theme[themeStyle].darkGray }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <>
                                                    {searchTerm.length > 3 && (
                                                        <InputAdornment position="end">
                                                            <img
                                                                src={close}
                                                                onClick={onTalentSearchClear}
                                                                alt="close icon"
                                                            />
                                                        </InputAdornment>
                                                    )}
                                                </>
                                            ),
                                        }}
                                        onChange={e => {
                                            onChange(e);
                                        }}
                                        sx={{
                                            pb: 1,
                                            width: '100%',
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Box>
                            </Stack>
                            {talentData && !isEmpty(talentData) && (
                                <Box display="flex" alignItems="flex-end" justifyContent="flex-end" width="100%">
                                    <ViewToggler />
                                </Box>
                            )}
                            {view === 'table' && (
                                <Table
                                    columns={columnsData}
                                    staticData={talentData}
                                    rowsPerPageOptions={[5, 10, 15, 30]}
                                    tableType="talent"
                                    rowHeight={90}
                                    headerHeight={65}
                                    textSx={{
                                        fontSize: 14,
                                    }}
                                    sx={{
                                        ...(isEmpty(talentData) && { minHeight: 450 }),
                                    }}
                                    slots={{ noRowsOverlay: NoTalentsData }}
                                    externalLoading={loadingSearch || loadingData}
                                />
                            )}
                            {talentData && !isEmpty(talentData) && view === 'grid' && (
                                <Box
                                    style={{
                                        width: '100%',
                                        height: 'fit-content',
                                        display: 'grid',
                                        gap: '15px',
                                        gridAutoRows: '1fr',
                                    }}
                                    sx={{
                                        gridTemplateColumns: {
                                            xs: 'repeat(2, 1fr)',
                                            sm: 'repeat(3, 1fr)',
                                            md: 'repeat(4, 1fr)',
                                            lg: 'repeat(5, 1fr)',
                                        },
                                    }}>
                                    {loadingSearch ? (
                                        <Spinner color="white" h={10} w={10} pos="absolute" top="30%" left="50%" />
                                    ) : (
                                        talentData.map(item => (
                                            <TalentsCard
                                                talent={item}
                                                key={item._id}
                                                onClickUserCard={() =>
                                                    handleUserSuggestionClick && handleUserSuggestionClick(item)
                                                }
                                                onDeleteTalent={onDeleteTalent}
                                                onEditTalent={({ note, tags }) =>
                                                    onEditTalent({ note, tags, id: item._id })
                                                }
                                            />
                                        ))
                                    )}
                                </Box>
                            )}
                            {talentData && !isEmpty(talentData) && (
                                <Table
                                    paginateOnly
                                    rowsPerPageOptions={[5, 10, 15, 30]}
                                    tableType="talent"
                                    imageType="talent"
                                    rowHeight={90}
                                    headerHeight={65}
                                    handleLoad={handleLoadingData}
                                />
                            )}
                            {(searchTerm || talentData) && isEmpty(talentData) && view === 'grid' && NoTalentsData()}
                        </Box>
                    </Container>
                );
            }}
        </ThemeContext.Consumer>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        text: {
            textAlign: 'left',
            fontSize: 24,
            fontStyle: 'normal',
            fontWeight: '600',
            color: theme[colorScheme].filters_white,
            fontFamily: FONT_ENUM.MAIN_BOLD,
        },
        container: {
            minHeight: '100vh',
        },
    });
export { TalentsContainer };
